import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import logo from "../assets/logo.png";
import smallLogo from "../assets/small.png";
export default function Header() {
  const location = useLocation();
  const navigate = useNavigate();

  const [menuOpen, setMenuOpen] = useState(false);
  const [hoveredItem, setHoveredItem] = useState(null);
  const [showFundraiseDropdown, setShowFundraiseDropdown] = useState(false);
  const [activeTab, setActiveTab] = useState("");

  const [user, setUser] = useState(null);
  const [profileSheetOpen, setProfileSheetOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  // Determine active tab based on current route
  useEffect(() => {
    const path = location.pathname;

    if (path === "/" || path.startsWith("/campaign")) {
      setActiveTab("home");
    } else if (path === "/about") {
      setActiveTab("about");
    } else if (path === "/contact") {
      setActiveTab("contact");
    } else if (path === "/start-fundraiser") {
      setActiveTab("fundraise");
    } else if (path === "/signin") {
      setActiveTab("signin");
    } else if (path === "/privacy-policy" || path === "/terms-and-conditions") {
      setActiveTab(""); // No active tab for legal pages
    } else {
      setActiveTab("");
    }
  }, [location]);

  // Intersection Observer for Campaigns section - highlights "Campaigns" tab when scrolled to
  useEffect(() => {
    const campaignsSection = document.querySelector("#campaigns-section");
    
    if (!campaignsSection) return;
    
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && location.pathname === "/") {
            setActiveTab("campaigns");
          }
        });
      },
      { threshold: 0.3 }
    );
    
    observer.observe(campaignsSection);
    
    return () => observer.disconnect();
  }, [location.pathname]);

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close all dropdowns when route changes
  useEffect(() => {
    setShowFundraiseDropdown(false);
    setMenuOpen(false);
  }, [location]);

  // Detect logged in user from sessionStorage
  useEffect(() => {
    const storedUser = sessionStorage.getItem("user");

    if (storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        setUser(parsedUser);
      } catch (err) {
        sessionStorage.removeItem("user");
        setUser(null);
      }
    } else {
      setUser(null);
    }
  }, [location.pathname]);

  // Prevent body scroll when profile sheet or mobile menu is open
  useEffect(() => {
    if (profileSheetOpen || menuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [profileSheetOpen, menuOpen]);

  const navItems = [
    {
      name: "Home",
      path: "/",
      tabKey: "home",
      isScroll: false,
    },
    {
      name: "Campaigns",
      path: "#campaigns-section",
      tabKey: "campaigns",
      isScroll: true,
    },
    {
      name: "About",
      path: "/about",
      tabKey: "about",
      isScroll: false,
    },
    {
      name: "Contact",
      path: "/contact",
      tabKey: "contact",
      isScroll: false,
    },
  ];

  // Purpose options (kept for non-logged in users)
  const purposeOptions = [
    {
      value: "medical",
      label: "Medical",
      icon: "fa-heart-pulse",
      color: "from-rose-500 to-red-500",
    },
    {
      value: "education",
      label: "Education",
      icon: "fa-graduation-cap",
      color: "from-blue-500 to-indigo-500",
    },
    {
      value: "emergency",
      label: "Emergency",
      icon: "fa-bolt",
      color: "from-amber-500 to-orange-500",
    },
  ];

  // Scroll handler for Campaigns tab
  const handleScrollToSection = (sectionId, e) => {
    if (e) e.preventDefault();
    
    // Close mobile menu if open
    setMenuOpen(false);
    closeAllDropdowns();
    
    // Check if we're on home page
    if (location.pathname !== "/") {
      // If not on home page, navigate to home page first, then scroll
      navigate("/");
      // Wait for navigation to complete, then scroll
      setTimeout(() => {
        const element = document.querySelector(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }, 150);
    } else {
      // Already on home page, scroll directly
      const element = document.querySelector(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }
  };

  // Handle purpose selection for non-logged in users only
  const handlePurposeSelect = (purpose) => {
    localStorage.setItem("preselectedPurpose", purpose.value);
    navigate("/start-fundraiser");
    setShowFundraiseDropdown(false);
    setMenuOpen(false);
  };

  // Handle general fundraiser click for non-logged in users
  const handleGeneralFundraiser = () => {
    localStorage.removeItem("preselectedPurpose");
    navigate("/start-fundraiser");
    setShowFundraiseDropdown(false);
    setMenuOpen(false);
  };

  // Handle start fundraiser click for logged in users
  const handleLoggedInFundraiser = () => {
    if (user) {
      navigate("/detailsScreen", { 
        state: { 
          userId: user.id || user.userId 
        } 
      });
    } else {
      navigate("/start-fundraiser");
    }
    setShowFundraiseDropdown(false);
    setMenuOpen(false);
  };

  const handleLogout = () => {
    sessionStorage.removeItem("user");
    localStorage.removeItem("completeUserData");
    setUser(null);
    setProfileSheetOpen(false);
    setMenuOpen(false);
    navigate("/");
  };

  // Store user data for Step1 when starting fundraiser
  const handleStartFundraiser = () => {
    if (user) {
      localStorage.setItem(
        "completeUserData",
        JSON.stringify({
          name: user.name || "",
          email: user.email || "",
          mobile: user.mobile || "",
          userId: user.id || user.userId || "",
        })
      );
    }
  };

  // Close all dropdowns
  const closeAllDropdowns = () => {
    setShowFundraiseDropdown(false);
    setHoveredItem(null);
  };

  // Handle mouse leave from navbar area
  const handleNavbarMouseLeave = () => {
    setShowFundraiseDropdown(false);
    setHoveredItem(null);
  };

  // Check if tab is active
  const isTabActive = (tabKey) => {
    return activeTab === tabKey;
  };

  // Safe user data access with fallbacks
  const getUserInitial = () => {
    if (!user) return "U";
    if (user.name && typeof user.name === 'string' && user.name.length > 0) {
      return user.name[0].toUpperCase();
    }
    if (user.mobile && typeof user.mobile === 'string') {
      return user.mobile.slice(-2);
    }
    return "U";
  };

  const getUserDisplayName = () => {
    if (!user) return "User";
    return user.name || user.email || "User";
  };

  const getUserEmail = () => {
    if (!user) return "";
    return user.email || "";
  };

  // Handle profile icon click
  const handleProfileClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    setProfileSheetOpen(true);
    setMenuOpen(false);
  };

  // Handle mobile fundraiser click
  const handleMobileFundraiserClick = () => {
    if (user) {
      navigate("/detailsScreen", { 
        state: { 
          userId: user.id || user.userId 
        } 
      });
    } else {
      navigate("/start-fundraiser");
    }
    setMenuOpen(false);
  };

  return (
    <>
      <nav
        className={`flex sticky top-0 z-50 items-center justify-between px-4 md:px-8 lg:px-12 py-2 transition-all duration-500 ${
          scrolled
            ? "bg-white/90 backdrop-blur-xl shadow-lg border-b border-white/20"
            : "bg-white/80 backdrop-blur-sm shadow-md"
        } w-full min-h-[60px]`}
        onMouseLeave={handleNavbarMouseLeave}
      >
        {/* Left Section: Logo + Navigation */}
        <div className="flex items-center gap-6 lg:gap-10">
          <Link
            to="/"
            onClick={closeAllDropdowns}
            className="relative group flex items-center overflow-visible h-14"
          >
     
            {/* Logo Icon */}
            <div className="relative z-30 flex items-center justify-center h-full">
              <img
                src={logo}
                alt="DonatePe Logo"
                className="h-16 w-auto object-contain"
              />
            </div>

            {/* Text Coming Out from behind logo */}
            <div className="relative overflow-visible flex items-center h-full ml-1">
              <h1 className="text-xl md:text-2xl lg:text-3xl font-bold tracking-tight text-black leading-none whitespace-nowrap pl-1 flex items-end">
                <span className="letter-animation letter-1">D</span>
                <span className="letter-animation letter-2">o</span>
                <span className="letter-animation letter-3">n</span>
                <span className="letter-animation letter-4">a</span>
                <span className="letter-animation letter-5">t</span>
                <span className="letter-animation letter-6">e</span>
                <span className="text-black flex items-end relative">
                  <span className="letter-animation letter-7">P</span>
                  <span className="letter-animation letter-8">e</span>
                  <span className="letter-animation letter-9 absolute -top-0.5 md:-top-1.5 -right-1.5 md:-right-3 text-[0.55em] md:text-[0.5em]">™</span>
                </span>
              </h1>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-1 lg:gap-2">
            {navItems.map((item) => (
              item.isScroll ? (
                // Scroll link for Campaigns
                <button
                  key={item.name}
                  onClick={(e) => handleScrollToSection(item.path, e)}
                  className={`relative px-4 py-2 rounded-full text-sm lg:text-base font-medium transition-all duration-300 group ${
                    isTabActive(item.tabKey)
                      ? "text-teal-700"
                      : "text-gray-600 hover:text-teal-600"
                  }`}
                  onMouseEnter={() => setHoveredItem(item.name)}
                  onMouseLeave={() => setHoveredItem(null)}
                >
                  {item.name}
                  {isTabActive(item.tabKey) ? (
                    <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-4 h-0.5 bg-gradient-to-r from-teal-500 to-emerald-500 rounded-full"></span>
                  ) : (
                    <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-4 h-0.5 bg-teal-500 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300 scale-0 group-hover:scale-100"></span>
                  )}
                </button>
              ) : (
                // Regular Link for other items
                <Link
                  key={item.name}
                  to={item.path}
                  className={`relative px-4 py-2 rounded-full text-sm lg:text-base font-medium transition-all duration-300 group ${
                    isTabActive(item.tabKey)
                      ? "text-teal-700"
                      : "text-gray-600 hover:text-teal-600"
                  }`}
                  onMouseEnter={() => setHoveredItem(item.name)}
                  onMouseLeave={() => setHoveredItem(null)}
                  onClick={closeAllDropdowns}
                >
                  {item.name}
                  {isTabActive(item.tabKey) ? (
                    <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-4 h-0.5 bg-gradient-to-r from-teal-500 to-emerald-500 rounded-full"></span>
                  ) : (
                    <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-4 h-0.5 bg-teal-500 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300 scale-0 group-hover:scale-100"></span>
                  )}
                </Link>
              )
            ))}
             {/* Small Client Logo - Next to Contact */}
            <div className="flex items-center justify-center ml-2">
              <img
                src={smallLogo}
                alt="Client Logo"
                className="h-8 w-8 object-contain"
              />
            </div>
          </div>
        </div>

        {/* Right Section: Actions */}
        <div className="flex items-center gap-2 md:gap-3 lg:gap-4">
          {/* Fundraise Button - Desktop */}
          <div className="hidden md:block relative">
            {user ? (
              <button
                onClick={handleLoggedInFundraiser}
                className="relative group px-5 py-2 bg-gradient-to-r from-teal-600 to-emerald-600 text-white rounded-full text-sm lg:text-base font-semibold shadow-lg hover:shadow-xl hover:shadow-teal-200/50 transition-all duration-300 hover:scale-105"
              >
                <span className="relative z-10">Start Fundraiser</span>
                <div className="absolute inset-0 rounded-full bg-gradient-to-r from-teal-500 to-emerald-500 blur-lg opacity-40 group-hover:opacity-60 transition-opacity duration-500"></div>
              </button>
            ) : (
              <>
                <button
                  className={`relative group flex items-center gap-2 px-5 py-2 rounded-full text-sm lg:text-base font-semibold transition-all duration-300 ${
                    isTabActive("fundraise")
                      ? "bg-gradient-to-r from-teal-600 to-emerald-600 text-white shadow-lg shadow-teal-200/50"
                      : "bg-white text-gray-700 border-2 border-gray-200 hover:border-teal-600 hover:text-teal-600 hover:shadow-lg"
                  }`}
                  onMouseEnter={() => {
                    setHoveredItem("fundraise");
                    setShowFundraiseDropdown(true);
                  }}
                  onClick={() => setShowFundraiseDropdown(!showFundraiseDropdown)}
                >
                  <span>Start Fundraiser</span>
                  <svg
                    className={`w-4 h-4 transition-transform duration-300 ${
                      showFundraiseDropdown ? "rotate-180" : ""
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2.5}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                  {!isTabActive("fundraise") && (
                    <div className="absolute inset-0 rounded-full bg-gradient-to-r from-teal-500/20 to-emerald-500/20 blur-md opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                  )}
                </button>
                {showFundraiseDropdown && (
                  <div
                    className="absolute right-0 mt-3 w-80 bg-white/95 backdrop-blur-xl rounded-2xl shadow-2xl border border-gray-100/50 overflow-hidden z-[60] animate-dropdown"
                    onMouseEnter={() => setShowFundraiseDropdown(true)}
                    onMouseLeave={() => setShowFundraiseDropdown(false)}
                  >
                    <div className="bg-gradient-to-r from-teal-600 to-emerald-600 px-5 py-4">
                      <h4 className="text-white font-semibold text-left text-sm flex items-center gap-2">
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2.5}
                            d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                          />
                        </svg>
                        Start fundraising for
                      </h4>
                    </div>
                    <div className="p-2 max-h-[320px] overflow-y-auto">
                      {purposeOptions.map((option) => (
                        <button
                          key={option.value}
                          className="w-full px-4 py-3 rounded-xl cursor-pointer transition-all duration-300 hover:bg-gradient-to-r hover:from-gray-50 hover:to-gray-50 group flex items-center justify-between"
                          onClick={() => handlePurposeSelect(option)}
                        >
                          <div className="flex items-center gap-3">
                            <div
                              className={`w-8 h-8 rounded-lg bg-gradient-to-r ${option.color} flex items-center justify-center text-white text-sm shadow-md group-hover:scale-110 transition-transform duration-300`}
                            >
                              <i className={`fas ${option.icon}`}></i>
                            </div>
                            <span className="font-medium text-gray-700 group-hover:text-gray-900">
                              {option.label}
                            </span>
                          </div>
                          <svg
                            className="w-4 h-4 text-gray-400 group-hover:text-teal-600 group-hover:translate-x-1 transition-all duration-300"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M9 5l7 7-7 7"
                            />
                          </svg>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          {/* User Menu - Desktop */}
          <div className="hidden md:flex items-center gap-2">
            {!user ? (
              <>
                <Link
                  to="/signin"
                  className={`relative px-5 py-2 rounded-full text-sm lg:text-base font-medium transition-all duration-300 group ${
                    isTabActive("signin")
                      ? "bg-gradient-to-r from-teal-50 to-emerald-50 text-teal-700 border border-teal-200"
                      : "text-gray-600 hover:text-teal-600 border-2 border-transparent hover:border-teal-200 hover:bg-teal-50/50"
                  }`}
                  onClick={closeAllDropdowns}
                >
                  Sign in
                  {isTabActive("signin") && (
                    <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-4 h-0.5 bg-teal-600 rounded-full"></span>
                  )}
                </Link>
                <Link
                  to="/start-fundraiser"
                  onClick={() => {
                    localStorage.removeItem("preselectedPurpose");
                    closeAllDropdowns();
                    handleStartFundraiser();
                  }}
                  className="relative group px-5 py-2 bg-gradient-to-r from-teal-600 to-emerald-600 text-white rounded-full text-sm lg:text-base font-semibold shadow-lg hover:shadow-xl hover:shadow-teal-200/50 transition-all duration-300 hover:scale-105"
                >
                  <span className="relative z-10">Sign up</span>
                  <div className="absolute inset-0 rounded-full bg-gradient-to-r from-teal-500 to-emerald-500 blur-lg opacity-40 group-hover:opacity-60 transition-opacity duration-500"></div>
                </Link>
              </>
            ) : (
              <button
                onClick={handleProfileClick}
                className="relative w-10 h-10 rounded-full bg-gradient-to-r from-teal-500 to-emerald-500 flex items-center justify-center text-white shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-110 group"
                aria-label="Open profile menu"
              >
                <span className="text-base font-bold">
                  {getUserInitial()}
                </span>
                <div className="absolute -bottom-1 -right-1 w-2.5 h-2.5 bg-emerald-500 rounded-full border-2 border-white animate-pulse"></div>
                <div className="absolute inset-0 rounded-full bg-gradient-to-r from-teal-500 to-emerald-500 blur-lg opacity-0 group-hover:opacity-60 transition-opacity duration-500"></div>
              </button>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="relative flex md:hidden w-10 h-10 items-center justify-center group z-[60]"
            onClick={() => {
              setMenuOpen(!menuOpen);
              closeAllDropdowns();
            }}
            aria-label="Toggle menu"
          >
            <div className="relative w-5 h-5 flex flex-col items-center justify-center gap-1">
              <span
                className={`block w-5 h-0.5 bg-gray-600 rounded-full transition-all duration-300 ${
                  menuOpen ? "rotate-45 translate-y-1.5" : ""
                }`}
              ></span>
              <span
                className={`block w-5 h-0.5 bg-gray-600 rounded-full transition-all duration-300 ${
                  menuOpen ? "opacity-0" : ""
                }`}
              ></span>
              <span
                className={`block w-5 h-0.5 bg-gray-600 rounded-full transition-all duration-300 ${
                  menuOpen ? "-rotate-45 -translate-y-1.5" : ""
                }`}
              ></span>
            </div>
          </button>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      {menuOpen && (
        <>
          <div
            className="fixed inset-0 bg-black/20 backdrop-blur-sm z-[55] animate-fadeIn"
            onClick={() => setMenuOpen(false)}
          ></div>

          <div className="fixed top-[60px] left-0 right-0 w-full bg-white z-[60] shadow-xl animate-slideDown border-t border-gray-100 max-h-[calc(100vh-60px)] overflow-y-auto">
            <div className="w-full px-3 py-3">
              {/* User Info - if logged in */}
              {user && (
                <div className="mb-3 p-3 bg-gradient-to-r from-teal-50 to-emerald-50 rounded-xl border border-teal-100">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-r from-teal-500 to-emerald-600 flex items-center justify-center text-white font-bold text-lg">
                      {getUserInitial()}
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold text-gray-900">{getUserDisplayName()}</p>
                      <p className="text-xs text-gray-600">{getUserEmail()}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Mobile Navigation Links */}
              <div className="space-y-0.5">
                {/* Home */}
                <Link
                  to="/"
                  onClick={() => setMenuOpen(false)}
                  className={`flex items-center w-full px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                    activeTab === "home"
                      ? "bg-gradient-to-r from-teal-50 to-emerald-50 text-teal-700 border border-teal-200"
                      : "text-gray-700 hover:bg-gray-50 hover:text-teal-600"
                  }`}
                >
                  Home
                </Link>

                {/* Campaigns - with scroll behavior */}
                <button
                  onClick={() => handleScrollToSection("#campaigns-section")}
                  className={`block w-full text-left px-4 py-3 rounded-xl text-base font-medium transition-all duration-300 ${
                    activeTab === "campaigns"
                      ? "bg-gradient-to-r from-teal-50 to-emerald-50 text-teal-700 border border-teal-200"
                      : "text-gray-700 hover:bg-gray-50 hover:text-teal-600"
                  }`}
                >
                  Campaigns
                </button>

                {/* About */}
                <Link
                  to="/about"
                  onClick={() => setMenuOpen(false)}
                  className={`flex items-center w-full px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                    activeTab === "about"
                      ? "bg-gradient-to-r from-teal-50 to-emerald-50 text-teal-700 border border-teal-200"
                      : "text-gray-700 hover:bg-gray-50 hover:text-teal-600"
                  }`}
                >
                  About
                </Link>

                {/* Contact */}
                <Link
                  to="/contact"
                  onClick={() => setMenuOpen(false)}
                  className={`flex items-center w-full px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                    activeTab === "contact"
                      ? "bg-gradient-to-r from-teal-50 to-emerald-50 text-teal-700 border border-teal-200"
                      : "text-gray-700 hover:bg-gray-50 hover:text-teal-600"
                  }`}
                >
                  Contact
                </Link>

                {/* Separator */}
                <div className="pt-2 mt-2 border-t border-gray-100"></div>
                
                {/* Sign In / Profile Section */}
                {!user ? (
                  <Link
                    to="/signin"
                    onClick={() => setMenuOpen(false)}
                    className={`flex items-center w-full px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                      activeTab === "signin"
                        ? "bg-gradient-to-r from-teal-50 to-emerald-50 text-teal-700 border border-teal-200"
                        : "text-gray-700 hover:bg-gray-50 hover:text-teal-600"
                    }`}
                  >
                    Sign In
                  </Link>
                ) : (
                  <>
                    <Link
                      to="/my-contributions"
                      onClick={() => setMenuOpen(false)}
                      className="block px-4 py-3 rounded-xl text-base font-medium text-gray-700 hover:bg-gray-50 hover:text-teal-600 transition-all duration-300"
                    >
                      My Contributions
                    </Link>

                    <Link
                      to="/my-fundraiser"
                      onClick={() => setMenuOpen(false)}
                      className="block px-4 py-3 rounded-xl text-base font-medium text-gray-700 hover:bg-gray-50 hover:text-teal-600 transition-all duration-300"
                    >
                      My Fundraisers
                    </Link>
                  </>
                )}
              </div>

              {/* Mobile Start New Fundraiser Button */}
              {user && (
                <div className="mt-6 pt-4 border-t border-gray-200">
                  <button
                    onClick={handleMobileFundraiserClick}
                    className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-teal-600 to-emerald-600 text-white rounded-xl font-medium hover:shadow-lg transition-all duration-300"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    <span>Start New Fundraiser</span>
                  </button>
                </div>
              )}

              {/* Mobile Logout */}
              {user && (
                <div className="mt-4">
                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-rose-50 text-rose-600 rounded-xl font-medium hover:bg-rose-100 transition-all duration-300"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                    </svg>
                    <span>Logout</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* Profile Right Sheet */}
      {profileSheetOpen && user && (
        <>
          <div
            className="fixed inset-0 bg-black/20 backdrop-blur-sm z-[9999] animate-fadeIn"
            onClick={() => setProfileSheetOpen(false)}
          ></div>

          <div className="fixed top-0 right-0 h-full w-[400px] max-w-[90%] bg-white/95 backdrop-blur-xl z-[9999] shadow-2xl rounded-l-3xl overflow-hidden animate-slideInRight border-l border-gray-100/50">
            <div className="bg-gradient-to-r from-teal-600 to-emerald-600 p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-full bg-white/20 backdrop-blur-sm border-2 border-white/50 flex items-center justify-center text-white text-2xl font-bold shadow-xl">
                    {getUserInitial()}
                  </div>
                  <div className="text-white">
                    <h3 className="text-xl font-bold">{getUserDisplayName()}</h3>
                    <p className="text-sm text-white/80">{getUserEmail()}</p>
                  </div>
                </div>
                <button
                  onClick={() => setProfileSheetOpen(false)}
                  className="w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center text-white hover:bg-white/30 transition-all duration-300"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3 p-6">
              {[]?.map((stat, i) => (
                <div key={i} className="bg-gray-50/80 backdrop-blur-sm rounded-xl p-4 text-center border border-gray-100/50">
                  <div className="w-8 h-8 mx-auto bg-gradient-to-r from-teal-500 to-emerald-500 rounded-lg flex items-center justify-center text-white text-sm mb-2">
                    <i className={`fas ${stat.icon}`}></i>
                  </div>
                  <h2 className="text-xl font-bold text-gray-800">{stat.value}</h2>
                  <p className="text-xs text-gray-500">{stat.label}</p>
                </div>
              ))}
            </div>

            <div className="px-6 space-y-2">
              {[
                {
                  label: "My Contributions",
                  icon: "fa-hand-holding-heart",
                  path: "/my-contributions",
                  color: "from-teal-500 to-emerald-500",
                },
                {
                  label: "My Fundraisers",
                  icon: "fa-flag",
                  path: "/my-fundraiser",
                  color: "from-amber-500 to-orange-500",
                },
              ].map((item) => (
                <button
                  key={item.label}
                  className="w-full flex items-center justify-between px-4 py-3.5 bg-gray-50/80 hover:bg-gray-100 rounded-xl transition-all duration-300 group"
                  onClick={() => {
                    setProfileSheetOpen(false);
                    navigate(item.path);
                  }}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-lg bg-gradient-to-r ${item.color} flex items-center justify-center text-white text-sm shadow-md group-hover:scale-110 transition-transform duration-300`}>
                      <i className={`fas ${item.icon}`}></i>
                    </div>
                    <span className="font-medium text-gray-700">{item.label}</span>
                  </div>
                  <svg className="w-5 h-5 text-gray-400 group-hover:text-teal-600 group-hover:translate-x-1 transition-all duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              ))}

              <button
                className="w-full flex items-center justify-between px-4 py-3.5 bg-rose-50/80 hover:bg-rose-100 rounded-xl transition-all duration-300 group mt-4"
                onClick={handleLogout}
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-r from-rose-500 to-red-500 flex items-center justify-center text-white text-sm shadow-md group-hover:scale-110 transition-transform duration-300">
                    <i className="fas fa-sign-out-alt"></i>
                  </div>
                  <span className="font-medium text-rose-600">Logout</span>
                </div>
                <svg className="w-5 h-5 text-rose-400 group-hover:text-rose-600 group-hover:translate-x-1 transition-all duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
              </button>
            </div>

            <div className="absolute bottom-0 left-0 right-0 p-6 text-center">
              <p className="text-xs text-gray-400">DonatePe • Making giving beautiful</p>
            </div>
          </div>
        </>
      )}

      <style>{`
        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes slideInRight {
          from {
            transform: translateX(100%);
          }
          to {
            transform: translateX(0);
          }
        }

        @keyframes dropdown {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes float {
          0%, 100% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-5px);
          }
        }

        @keyframes smoothLetterReveal {
          0% {
            opacity: 0;
            transform: translateX(-40px) translateY(8px) scale(0.7) rotate(-4deg);
            filter: blur(8px);
            text-shadow: 0 0 15px rgba(20, 184, 166, 0.6);
          }
          30% {
            opacity: 0.9;
            transform: translateX(-5px) translateY(0) scale(1.15) rotate(2deg);
            filter: blur(0);
            text-shadow: 0 0 25px rgba(20, 184, 166, 0.9);
          }
          50% {
            transform: translateX(0) translateY(0) scale(1.08) rotate(-1deg);
            text-shadow: 0 0 20px rgba(20, 184, 166, 0.7);
          }
          70% {
            transform: translateX(0) translateY(0) scale(0.99) rotate(0.5deg);
          }
          100% {
            opacity: 1;
            transform: translateX(0) translateY(0) scale(1) rotate(0);
            filter: blur(0);
            text-shadow: 0 0 0 transparent;
          }
        }

        .letter-animation {
          display: inline-block;
          opacity: 0;
          animation: smoothLetterReveal 2.2s cubic-bezier(0.22, 0.78, 0.45, 1.02) infinite;
          animation-direction: alternate;
          will-change: transform, opacity, filter;
        }

        .letter-1 { animation-delay: 0s; }
        .letter-2 { animation-delay: 0.12s; }
        .letter-3 { animation-delay: 0.24s; }
        .letter-4 { animation-delay: 0.36s; }
        .letter-5 { animation-delay: 0.48s; }
        .letter-6 { animation-delay: 0.6s; }
        .letter-7 { animation-delay: 0.72s; }
        .letter-8 { animation-delay: 0.84s; }

        .group:hover .letter-animation {
          animation: smoothLetterReveal 1.8s cubic-bezier(0.22, 0.78, 0.45, 1.02) infinite;
          animation-direction: alternate;
        }

        .animate-slideDown { animation: slideDown 0.3s ease-out; }
        .animate-fadeIn { animation: fadeIn 0.3s ease-out; }
        .animate-slideInRight { animation: slideInRight 0.3s cubic-bezier(0.2, 0.9, 0.3, 1); }
        .animate-dropdown { animation: dropdown 0.3s cubic-bezier(0.2, 0.9, 0.3, 1); }
        .animate-float { animation: float 3s ease-in-out infinite; }
      `}</style>
    </>
  );
}